import { Badge as AntdBadge, Layout, Menu as AntMenu, Space } from 'antd';
import styled, { css } from 'styled-components';
import { ifProp } from 'styled-tools';
import { Avatar } from 'components/atoms/avatar';
import { Button } from 'components/atoms/button';
import { NotificationsBadge } from 'components/molecules/notifications-badge';
import { MenuStyle } from 'components/organisms/projects/project-sidebar/menu.style';
import {
  BodyShort02,
  ProductiveHeading06,
  SingleLineEllipsis,
  Text02,
  Text03,
} from 'components/styles';

const SIDEBAR_WIDTH = 256;

export const BackgroundStyle = css`
  background: linear-gradient(
    0deg,
    var(--gray-100) 80%,
    var(--brand-color) 100%
  );
`;

export const Sider = styled(Layout.Sider).attrs({
  collapsedWidth: 24,
  collapsible: false,
  defaultCollapsed: false,
  theme: 'dark',
  width: SIDEBAR_WIDTH,
})`
  border-right: solid 1px var(--border-color);
  z-index: calc(var(--zindex-layer-09) + 1);
  top: 0;
  height: 100vh;
  overflow: auto;
  position: fixed;
  left: 0;
  ${BackgroundStyle};
`;

export const Menu = styled(AntMenu).attrs({ theme: 'dark' })`
  ${MenuStyle};
  --item-hover-color: rgba(255, 255, 255, 0.1);
  --text-color: var(--gray-10);
  &.ant-menu-dark {
    background: none;
    &.ant-menu-dark:not(.ant-menu-horizontal) {
      .ant-menu-item-selected {
        background-color: var(--item-hover-color);
      }
    }
    .ant-menu-submenu-open {
      color: var(--text-color);
    }
    .ant-menu-sub {
      background: inherit;
    }
    &.ant-menu-inline .ant-menu-item::after {
      border-right: 4px solid var(--brand-color);
    }
    .ant-menu-item {
      color: var(--text-color);
      & > span > a {
        color: var(--text-color);
      }
      &:not(.ant-menu-item-selected):hover {
        background-color: var(--item-hover-color);
        color: var(--text-color);
        & a {
          color: var(--white-100);
        }
      }
    }
    .ant-menu-submenu-title:hover {
      background-color: var(--item-hover-color);
      color: var(--text-color);
    }
  }
  & .ant-menu-item.ant-menu-item-only-child {
    //position: fixed;
    //bottom: var(--spacing-05);
  }
`;

export const MenuItemLg = styled(Menu.Item)`
  &&& {
    height: 40px;
    ${BodyShort02};
  }
`;

export const IntendedMenuItem = styled(Menu.Item)`
  &&& {
    padding-left: var(--spacing-07) !important;
  }
`;

export const LogoContainer = styled(Space)`
  height: 116px;
  width: 100%;
  align-items: center;
  justify-content: center;
  display: flex;
  padding: var(--spacing-07);
`;

export const SubMenu = styled(Menu.SubMenu).attrs({ theme: 'dark' })`
  .ant-menu-submenu-title {
    padding-left: var(--spacing-07) !important;
  }
  .ant-menu.ant-menu-sub .ant-menu-item a {
    width: 144px;
    ${SingleLineEllipsis};
    display: block;
  }
`;

export const SpaceWrapper = styled.div`
  padding-left: var(--spacing-09);
  ${Text02};
  display: flex;
  align-items: center;
  min-height: 32px;
`;

export const Empty = styled(SpaceWrapper)`
  ${Text03};
`;

export const ProjectName = styled.div`
  ${ProductiveHeading06};
`;

export const ProjectLogoStyle = css`
  .ant-avatar {
    flex-shrink: 0;
    margin-right: var(--layout-01);
    width: 20px;
    height: 20px;
    .ant-avatar-string {
      font-weight: bold;
    }
  }
  .ant-avatar + span {
    width: 100%;
  }
`;

export const ProjectMenuItem = styled(MenuItemLg)`
  ${ProjectLogoStyle};
`;

export const ProjectNameWrapper = styled.div`
  display: flex;
  align-items: center;
  padding-left: var(--spacing-05);
  height: 40px;
  ${ProjectLogoStyle};
  ${ProjectName} {
    width: 200px;
    ${SingleLineEllipsis};
    color: var(--text-color);
  }
`;

export const Trigger = styled.div<{ hasMore: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  ${ProjectName} {
    width: ${ifProp('hasMore', '172px', '192px')};
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

export const DropdownMenu = styled(Menu)`
  max-height: 480px;
  max-width: 320px;
  overflow-y: auto;
  overflow-x: hidden;
`;

export const DropdownMenuItem = styled(Menu.Item)`
  display: flex;
  align-items: center;
  & ${ProjectName} {
    margin-left: var(--spacing-03);
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
`;

export const Divider = styled(Menu.Divider)`
  visibility: hidden;
  margin: var(--spacing-04);
`;

export const Logo = styled(Avatar).attrs({ size: 44, shape: 'square' })`
  &&& {
    background: none;
    border: 1px solid var(--white-10);
  }
`;

export const ChatWithUsButton = styled(Button)`
  color: var(--white-100);
  display: block;
  text-align: center;
  width: 100%;
`;

export const InfoRight = styled.div`
  display: flex;
  justify-content: space-between;
  flex: 1;
`;
export const BadgeStyles = css`
  color: var(--white-100);
  margin-right: var(--layout-01);
  sup {
    right: 0;
  }
`;

export const NotifBadge = styled(NotificationsBadge)`
  ${BadgeStyles};
`;
export const StyledBadge = styled(AntdBadge)`
  margin-left: 126px;
  sup {
    background: var(--ozone-red-background);
    box-shadow: none;
    top: 6px;
  }
`;
export const Loader = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;
export const LoadingMessage = styled.div`
  ${Text03};
`;

export const StyledSidebar = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 0;
  min-width: 0;
  height: 100%;
  overflow-x: hidden;
  padding-top: var(--spacing-05);
`;

export const Header = styled.div``;

export const Navigation = styled.div`
  height: auto;
  min-height: 0;
  width: 100%;
  flex: 1;
  position: relative;
  overflow-x: hidden;
  overflow-y: auto;
  padding-bottom: var(--spacing-11);
`;

export const Footer = styled.div`
  position: relative;
`;
